import { anyLeague } from "../../../classificators/league/index.js";
import { IterableDataProcessor, RankingStat } from "../../../entities/index.js";
import { BattingRankingStats, DataSection, ResultData, ScoringData, StatType, RankingScopeContext, PitchingRankingStats } from "../../../enums/index.js";
import EnumValueProvider from "../../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./../MultiRankingStatCalculator.js";
import NumberFormatter from "../../../formatters/NumberFormatter.js";
import ResultsSlice from "../../../valueObjects/ResultsSlice.js";
import satisfiesAll from "../../../classificators/satisfiesAll.js";
import battingScopeContext from "../../../classificators/resultsSlice/battingScopeContext.js";
import pitchingScopeContext from "../../../classificators/resultsSlice/pitchingScopeContext.js";
import RankingStatCaseVerifier from "../../../validators/RankingStatCaseVerifier.js";
import * as PlateAppearanceResultClassifiers from '../../../classificators/plateAppearanceResult/index.js';
import sessionWithStatGrabScoring from "../../../classificators/session/sessionWithStatGrabScoring.js";
import resultsSliceBeforePitchType from "../../../classificators/resultsSlice/resultsSliceBeforePitchType.js";
import PlateAppearanceResultToBaseNumberConverter from "../../../converters/PlateAppearanceResultToBaseNumberConverter.js";

class NewAtBatsCalculator extends MultiRankingStatCalculator {
    constructor(
        context, customResultsProcessor, rankingStatCaseVerifier, numberFormatter, skipCache,
        atBatClassifier, hitClassifier
    ) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof IterableDataProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from IterableDataProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(atBatClassifier) !== 'undefined' && typeof(atBatClassifier) !== 'function') {
            throw new TypeError('atBatClassifier must be an function or undefined');
        }
        if (typeof(hitClassifier) !== 'undefined' && typeof(hitClassifier) !== 'function') {
            throw new TypeError('hitClassifier must be an function or undefined');
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Batting.key ? [ 
                BattingRankingStats.AtBats, BattingRankingStats.BattingAverage,
                BattingRankingStats.SluggingPercentage, BattingRankingStats.IsolatedPower
            ] : [
                PitchingRankingStats.AtBats, PitchingRankingStats.BattingAverage,
                PitchingRankingStats.SluggingPercentage, PitchingRankingStats.IsolatedPower
            ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague,
                sessionWithStatGrabScoring,
                satisfiesAll(
                    context === RankingScopeContext.Batting.key ? battingScopeContext : pitchingScopeContext,
                    resultsSliceBeforePitchType
                )
            ),
            skipCache
        );

        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new IterableDataProcessor(
            new EnumValueProvider()
        );
        this.atBatClassifier = atBatClassifier || PlateAppearanceResultClassifiers.classifiesAsAtBat;
        this.hitClassifier = hitClassifier || PlateAppearanceResultClassifiers.classifiesAsHit;
        this.plateAppearanceResultToBaseNrConverter = PlateAppearanceResultToBaseNumberConverter;
        this.context = context;
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const results = slice.getSmallestSlice();

        const stats = this.resultsProcessor.calcStats(
            [StatType.Ratio.key],
            results,
            // TODO - simplify it...
            [ [ [] ].concat(
                ResultData.getNewDataPath(ScoringData.PlateAppearanceResult.key, DataSection.Scoring.key)
            ) ],
            false
        );

        const output = {};
        let atBats = 0;
        let hits = 0;
        let totalBases = 0;

        if (!stats || !stats.hasOwnProperty(ScoringData.PlateAppearanceResult.key) || !stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key]){
            return output;
        }

        for (const result in stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key]) {
            if (!Object.hasOwnProperty.call(stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key], result)) {
                continue;
            }

            const count = stats[ScoringData.PlateAppearanceResult.key][StatType.Ratio.key][result].count;
            //hits and home runs are only common here
            if (this.atBatClassifier(result)) {
                // TODO - check for substitution
                // MLB Official Rules 2018 version, Rule 9.15
                // (b) When a batter leaves the game with two strikes against him, and the substitute batter completes a strikeout, the official scorer shall charge the strikeout and the time at bat to the first batter. If the substitute batter completes the turn at bat in any other manner, including a base on balls, the official scorer shall score the action as having been that of the substitute batter.
                atBats += count;
            }
            if (this.hitClassifier(result)) {
                hits += count;
            }

            totalBases += this.plateAppearanceResultToBaseNrConverter(result) * count;
        }

        // now include hits and total bases
        if (atBats > 0) {
            const atBatsParam = this.supportedParams[0];
            output[atBatsParam.key] = RankingStat.fromParameter(
                atBatsParam, this.numberFormatter.format(atBats), atBats
            );

            const battingAvgParam = this.supportedParams[1];
            output[battingAvgParam.key] = RankingStat.fromParameter(
                // note - Presented as a 3 digit decimal, not multiplied by 100% 
                battingAvgParam, this.numberFormatter.format(hits / atBats), atBats
            );

            const slgParam = this.supportedParams[2];
            output[slgParam.key] = RankingStat.fromParameter(
                // note - Presented as a 3 digit decimal, not multiplied by 100% 
                slgParam, this.numberFormatter.format(totalBases / atBats), atBats
            );

            // https://www.mlb.com/glossary/advanced-stats/isolated-power
            const isoParam = this.supportedParams[3];
            output[isoParam.key] = RankingStat.fromParameter(
                // note - Presented as a 3 digit decimal, not multiplied by 100% 
                isoParam, this.numberFormatter.format((totalBases - hits) / atBats), atBats
            );
        }

        return output;
    }
}

export default NewAtBatsCalculator;